import React from 'react';
import { HomeButton } from "../components/HomeButton"
import { Query_t, SearchPage, SearchResponse_t } from './Search';
import { sleep } from '../utils';


export interface Demo_t {
  input: string,
  query: Query_t,
  response: SearchResponse_t,

  setInput: (input: string) => void,
  setQuery: (query: Query_t) => void,
  setResponse: (response: SearchResponse_t) => void,
  setLoading: (loading: boolean) => void,
}

export const AboutPage: React.FC<{}> = () => {

  const [input1, setInput1] = React.useState<string>("");
  const [query1, setQuery1] = React.useState<Query_t | null>(null);
  const [searchResponse1, setSearchResponse1] = React.useState<SearchResponse_t | null>(null);
  const [loading1, setLoading1] = React.useState<boolean>(false);

  const [input2, setInput2] = React.useState<string>("");
  const [query2, setQuery2] = React.useState<Query_t | null>(null);
  const [searchResponse2, setSearchResponse2] = React.useState<SearchResponse_t | null>(null);
  const [loading2, setLoading2] = React.useState<boolean>(false);

  const [input3, setInput3] = React.useState<string>("");
  const [query3, setQuery3] = React.useState<Query_t | null>(null);
  const [searchResponse3, setSearchResponse3] = React.useState<SearchResponse_t | null>(null);
  const [loading3, setLoading3] = React.useState<boolean>(false);

  const demo1: Demo_t = {
    input: "search ranking algorithms similar to PageRank that rely on crawled metadata. before deep learning",
    query: {
      filters: [
        { table: "patents", column: "year", comparison: "[1990, 2012]", fuzzy: false },
        { table: "academic papers", column: "year", comparison: "[1990, 2012]", fuzzy: false },
        { table: "technical blog posts", column: "year", comparison: "[1990, 2012]", fuzzy: false },
        { table: "results", column: "description", comparison: "search ranking algorithms similar to PageRank that rely on crawled metadata", fuzzy: true },
      ],
      category: "Technical" as const
    },
    response: {
      hits: [
        {
          url: "https://www.researchgate.net/publication/200111112_Adaptive_on-line_page_importance_computation",
          title: "Adaptive on-line page importance computation",
          snippet: "We next describe (variants of) an algorithm, namely Adaptive OPIC, that compute(s) page importance based on a time window",
          rangesToBold: [{ min: 72, max: 121 }],
          year: 2003,
        },
        {
          url: "https://patentimages.storage.googleapis.com/cf/17/88/11ab2b3e40b66b/US7899807.pdf",
          title: "SYSTEM AND METHOD FOR CRAWL ORDERING BY SEARCH IMPACT - Yahoo!",
          snippet: "comparing a word-level n-gram representing a query string in a particular need query and a word-level n-gram representing an anchortext string",
          rangesToBold: [{ min: 12, max: 57 }, { min: 91, max: 143 }],
          year: 2011,
        },
        {
          url: "https://patents.google.com/patent/US8725726B1/en?q=~patent%2fUS7899807B2&peid=64d0375f3d290%3A12f%3A39c0c7d0",
          snippet: "ranks to nodes in a linked database. Such as any database of documents containing citations, the worldwide web or any other hypermedia database. The rank assigned to a document is a calculated from the ranks of documents citing it",
          rangesToBold: [{ min: 0, max: 36 }, { min: 97, max: 110 }, { min: 149, max: 230 }],
          title: "SCORING DOCUMENTS IN A LINKED DATABASE",
          year: 2001,
        },
        {
          url: "https://snap.stanford.edu/class/cs224w-readings/Brin98Anatomy.pdf",
          snippet: "there are new technical challenges involved with using the additional information present in hypertext to produce better search results. This paper addresses this question of how to build a practical large-scale system which can exploit the additional information present in hypertext",
          rangesToBold: [{ min: 49, max: 136 }, { min: 229, max: 284 }],
          title: "The anatomy of a large-scale hypertextual Web search engine",
          year: 1998,
        }
      ],
      response: `Several search ranking algorithms rely on metadata and the network structure of the web rather than the actual text within a document. By leveraging these "content-independent" elements, engines can rank pages before even crawling their internal text.

Here are some prominent methods and concepts based on the provided documents:

* **Extrinsic Relationship Ranking (PageRank):** As described in "Scoring Documents in a Linked Database," the foundational PageRank algorithm operates by evaluating the web's link structure. The patent states, "Rather than determining relevance from the intrinsic content of a document... the present method determines importance from the extrinsic relationships between documents". It operates on the idea that highly cited documents are more important, especially if the citing documents themselves are highly ranked.


* **OPIC (On-line Page Importance Computation):** Detailed in "Adaptive on-line page importance computation," this is an alternative to standard off-line PageRank that works continuously as the web is crawled. Instead of computing a massive link matrix all at once, OPIC uses a running credit system: "Intuitively speaking, some 'cash' is initially distributed to each page and each page when it is crawled distributes its current cash equally to all pages it points to".


* **Anchor Text Propagation:** As noted in "The anatomy of a large-scale hypertextual Web search engine," search engines associate the text of a hyperlink (the anchor) not just with the page it is on, but with the page it points to. This is incredibly useful for non-text documents because "anchors may exist for documents which cannot be indexed by a text-based search engine, such as images, programs, and databases".


* **Content-Independent Feature Scoring:** Algorithms often use a variety of metadata beyond just links. "System and Method for Crawl Ordering by Search Impact" highlights that "content-independent features, such as inlinks/PageRank and referring anchortext, tend to be heavily weighted in the overall scoring procedure". Other similar features include URL strings and host affiliations.



Would you like to explore the mathematical formulas behind OPIC, or dive deeper into how search engines order their crawling queues based on these scores?
`,
    },

    setInput: setInput1,
    setQuery: setQuery1,
    setResponse: setSearchResponse1,
    setLoading: setLoading1,
  };

  const demo2: Demo_t = {
    input: "My company has offered to re-price my ISO options lower, what are the tax implications?",
    query: {
      filters: [
        { table: "articles", column: "source", comparison: "Bloomberg | Investopedia | Harvard Law | Cornell Law | Columbia Law | Harvard Business | UCLA Law", fuzzy: false },
        { table: "results", column: "text", comparison: "ISO option reprice tax implications", fuzzy: true },
      ],
      category: "Finance" as const,
    },
    response: {
      hits: [
        {
          url: "https://clsbluesky.law.columbia.edu/2016/03/01/davis-polk-discusses-down-round-financings-of-private-companies-considerations-for-outstanding-equity-compensation-awards/",
          title: "Considerations for Outstanding Equity Compensation Awards",
          snippet: "for incentive stock options (ISOs), a repricing or exchange generally will restart the ISO holding period",
          rangesToBold: [{ min: 70, max: 105 }],
          year: 2016,
        },
        {
          url: "https://corpgov.law.harvard.edu/2022/05/28/repricing-underwater-options/",
          title: "Repricing Underwater Options",
          snippet: "A reduction in the exercise price of an incentive stock option (“ISO”) would be considered a “modification” akin to a new grant under applicable tax laws. [29] The new grant of an ISO restarts the holding periods required for beneficial tax treatment of shares purchased upon exercise of the ISO",
          rangesToBold: [{ min: 0, max: 153 }],
          year: 2022,
        },
        {
          url: "https://corpgov.law.harvard.edu/2020/05/02/stock-option-repricing-considerations-during-the-pandemic/",
          snippet: "In addition, under the ISO rules, a stock option repricing offer that remains open for more than 30 calendar days will generally disqualify the ISO and convert it to a nonqualified stock option.",
          rangesToBold: [{ min: 129, max: 194 }],
          title: 'Stock Option Repricing Considerations During the Pandemic',
          year: 2020,
        },
        {
          url: "https://repository.uclawsf.edu/cgi/viewcontent.cgi?params=/context/hastings_business_law_journal/article/1260/&path_info=4._STOCK_BASED_COMPENSATION_IN_STARTUPS.pdf",
          snippet: "The pre-existing options can either be cancelled and replaced with the issuance of new options or amended with a lower exercise price.174 There are also no tax implications to this method if the new exercise price is equal to the fair market value",
          rangesToBold: [{ min: 138, max: 247 }],
          title: 'Stock-Based Compensation in Startups: Employee Implications and Potential Solutions',
          year: 2024,
        },
      ],
      response: `Repricing Incentive Stock Options (ISOs) involves several specific tax considerations under the Internal Revenue Code. When a company lowers the exercise price of an ISO, the IRS generally views this as the cancellation of the old option and the grant of a new one.

According to the provided documents, here are the primary tax implications:

### 1. Restarting the Holding Period

The most significant impact of an ISO repricing is the restart of the mandatory holding periods required to receive favorable capital gains tax treatment. To qualify as a "statutory" ISO, you must hold the shares for at least two years from the date of grant and one year from the date of exercise.

As noted in **Stock Option Repricing Considerations During the Pandemic**:

> "For tax purposes, the repricing of an ISO is considered the grant of a new option... This means that the ISO holding period requirements (two years from the date of grant and one year from the date of exercise) must be satisfied with respect to the new grant in order for the optionee to receive favorable ISO tax treatment."

If you sell the shares before these new timelines are met, it is considered a "disqualifying disposition," and the gain is typically taxed as ordinary income rather than capital gains.

### 2. The $100,000 Limit

ISO treatment is limited to $100,000 worth of stock (based on the fair market value at the time of grant) that can become exercisable for the first time in any calendar year. Repricing can trigger this limit.

**Stock Option Repricing Considerations During the Pandemic** explains:

> "the $100,000 limit on the value of ISOs that can first become exercisable in any calendar year will apply to the repriced option. To the extent the $100,000 limit is exceeded, the option will be treated as a non-qualified stock option."

### 3. Fair Market Value (FMV) Requirement

For an ISO to maintain its tax-advantaged status, the new exercise price cannot be lower than the current value of the stock on the day of the repricing.

**Repricing Underwater Options** states:

> "If an ISO is repriced, it is treated as a new grant for purposes of the ISO rules... As a result, the ISO must be priced at no less than the fair market value on the date of the repricing."

### 4. Alternative Minimum Tax (AMT) Considerations

While the repricing itself is not a taxable event, the eventual exercise of the new ISO may still trigger AMT. This occurs if there is a "spread" between the new, lower exercise price and the fair market value at the time of exercise.

**Stock-Based Compensation in Startups: Employee Implications and Potential Solutions** notes:

> "The 'spread'—the difference between the fair market value of the stock at the time of exercise and the exercise price—is considered an adjustment for the purposes of the Alternative Minimum Tax (AMT)."

### 5. Potential Conversion to Non-Qualified Options

Because of the complexities mentioned above, some companies may choose to convert ISOs into Non-Qualified Stock Options (NSOs) during a repricing to avoid the strict ISO "modification" rules.

**Considerations for Outstanding Equity Compensation Awards** points out:

> "Because a repricing of an ISO is treated as a modification... companies often choose to treat the repriced ISO as a non-qualified stock option ('NSO') to avoid the negative tax consequences for the employee, such as the restart of the ISO holding period."

In summary, while a lower price makes the options more valuable, it restarts your clock for long-term capital gains and may shift some of your options into the "Non-Qualified" category if you exceed the $100,000 annual limit.
`,
    },

    setInput: setInput2,
    setQuery: setQuery2,
    setResponse: setSearchResponse2,
    setLoading: setLoading2,
  };

  const demo3: Demo_t = {
    input: "I'm looking for a song called Mexico by a female artist. The album also has a song called Wish You Were Here",
    query: {
      filters: [
        { table: "songs", column: "name", comparison: "Mexico", fuzzy: false },
        { table: "albums.songs", column: "name", comparison: "Wish You Were Here", fuzzy: false },
        { table: "results.artist", column: "description", comparison: "Female", fuzzy: true },
      ],
      category: "Music" as const
    },
    response: {
      hits: [
        {
          url: "https://open.spotify.com/album/4qWdknSb1huxeJB92IeKdY",
          snippet: "Snowpine Tracks: Unwell Wish You Were Here Who's Gonna Save You Now Mexico",
          rangesToBold: [{ min: 24, max: 42 }, { min: 68, max: 75 }],
          title: "Snowpine - Album by Charlie Collins",
        },
        {
          url: "https://www.allmusic.com/artist/charlie-collins-mn0004845588#biography",
          snippet: "Australian singer and songwriter Charlie Collins is known for her moody, textured indie pop. She first garnered acclaim",
          rangesToBold: [{ min: 11, max: 92 }],
          title: 'Charlie Collins Songs, Albums, Reviews, Bio & ... | AllMusic',
        },
        {
          url: "https://en.wikipedia.org/wiki/Charlie_Collins_(musician)",
          snippet: "For her solo debut album, Snowpine, she was nominated for the 2019 ARIA Award for Best Country Album. Her second studio album",
          rangesToBold: [{ min: 4, max: 34 }],
          title: 'Charlie Collins (musician) - Wikipedia',
        },
      ],
      response: `The song "Mexico" is by the Australian singer-songwriter **Charlie Collins**. It appears on her 2019 debut solo album titled ***Snowpine***, which also includes the track "Wish You Were Here."

**About the Artist and Album:**

* **Charlie Collins:** Before her solo career, she was a member of the indie pop band Tigertown.
* **Snowpine (2019):** This album is known for its blend of indie pop and rootsy country influences. It won the ARIA Award for Best Country Album and the AIR Award for Best Independent Country Album.
* **Tracklist:** In addition to "Mexico" and "Wish You Were Here," the album features songs such as "Unwell," "Who's Gonna Save You Now," and "Beautifully Blind."

Would you like to know more about Charlie Collins?
`,
    },

    setInput: setInput3,
    setQuery: setQuery3,
    setResponse: setSearchResponse3,
    setLoading: setLoading3,
  };



  React.useEffect(() => {
    const fn = async (demo: Demo_t) => {
      let currentText = "";
      for (let i = 0; i < demo.input.length; i++) {
        currentText += demo.input[i];
        demo.setInput(currentText);

        // this simulates the delay of typing
        await new Promise((resolve) => setTimeout(resolve, 25));
      }

      demo.setLoading(true);

      await sleep(1000);
      demo.setQuery(demo.query);
      await sleep(1000);
      demo.setResponse(demo.response);

      demo.setLoading(false);
    };

    fn(demo1).then(() => fn(demo2).then(() => fn(demo3)));
  }, []);

  return (
    <div className='App-no-center'>
      <HomeButton />
      <div style={{ width: "50%" }}>
        <h1>A New Way to Search</h1>
        <p>
          Find what you're looking for with <b>dynamic agentic query generation</b>
        </p>
        <p>Use <b>precise filters</b> and <b>fuzzy search</b></p>
        <p>Cut through the slop with trusted relevant sources</p>
        <p>Interested? We thought so</p>
      </div>
      <div>
        <button onClick={() => (document.location.href = '#/signup')}>
          Sign up for our beta
        </button>
      </div>
      <h1>Demos</h1>
      <hr style={{ width: '50%' }} />
      <h2>Research a Niche Technical Topic</h2>
      <SearchPage
        input={input1}
        response={searchResponse1}
        query={query1}
        loading={loading1}
      />
      <hr style={{ width: '50%', marginTop: "40px", marginBottom: "20px" }} />
      <h2>Use Trusted Sources When You Have to Get it Right</h2>
      <SearchPage
        input={input2}
        response={searchResponse2}
        query={query2}
        loading={loading2}
      />
      <hr style={{ width: '50%', marginTop: "40px", marginBottom: "20px" }} />
      <h2>Find Obscure Sources When Filters Matter</h2>
      <SearchPage
        input={input3}
        response={searchResponse3}
        query={query3}
        loading={loading3}
      />
      <hr style={{ width: '50%', marginTop: "40px" }} />
      <h1>Want More?</h1>
      <div style={{ marginBottom: "200px", paddingTop: "20px" }}>
        <button onClick={() => (document.location.href = '#/signup')}>
          Sign up for our beta
        </button>
        <p>contact@truesearcher.com</p>
      </div>

    </div>
  )
};